import type {
  EpocusEncounter,
  EpocusRequestPriority,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import type { EpocusExam } from "$global-providers/rxdb-provider/collections/epocus-exam";
import {
  EXAM_TYPES,
  type ExamType,
  NORMAL_WORKSHEET_BY_TYPE,
  usesMultiIndication,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { nanoid } from "nanoid";
import type { RxCollection } from "rxdb";
import { isOfferableExamType } from "../worksheets/us/us-definitions";
import { mintOrderAccession } from "./mint-order-accession";

export type ScanRequestDraft = {
  elephantPatientId: string | null;
  patientName: string | null;
  patientDob: string | null;
  patientMrn: string | null;
  requestedExamType: ExamType;
  indication: string | null;
  requestPriority: EpocusRequestPriority;
  requestNotes: string | null;
  performerId: string | null;
};

const syncableMeta = (userId: string, now: string) => ({
  createdAt: now,
  createdByUserId: userId,
  updatedAt: now,
  updatedByUserId: userId,
});

const asExamType = (value: string | null): ExamType | null =>
  EXAM_TYPES.includes(value as ExamType) &&
  isOfferableExamType(value as ExamType)
    ? (value as ExamType)
    : null;

/**
 * `<input type="date">` yields a bare `YYYY-MM-DD`, which the `DateTime` scalar
 * rejects — and a rejected row is not a dropped field: RxDB retries it forever
 * and, because push is ordered per collection, every later encounter write
 * queues behind it until the local database is wiped. A date-only string parses
 * as UTC midnight, so widening it here cannot shift the date either way.
 */
const asDateTime = (value: string | null): string | null => {
  if (!value) return null;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

/**
 * The accession is minted here, not left to the server. An encounter is a
 * client-authored document, so a server that filled the field in flight would
 * be changing a record the client has no way to learn had changed. The server
 * keeps a supplied accession and only mints when one is absent, so authoring it
 * here leaves the push resolver with just its worklist-entry side-effect.
 */
export const createScanRequest = async ({
  encounterCollection,
  userId,
  programId,
  draft,
  now = new Date().toISOString(),
}: {
  encounterCollection: RxCollection<EpocusEncounter>;
  userId: string;
  programId: string | null;
  draft: ScanRequestDraft;
  now?: string;
}): Promise<string> => {
  const encounterId = nanoid();
  const accessionNumber = await mintOrderAccession(encounterCollection);

  await encounterCollection.insert({
    id: encounterId,
    status: "ordered",
    accessionNumber,
    elephantPatientId: draft.elephantPatientId,
    patientName: draft.patientName,
    patientDob: asDateTime(draft.patientDob),
    patientMrn: draft.patientMrn,
    indication: draft.indication,
    requestedById: userId,
    requestedAt: now,
    requestedExamType: draft.requestedExamType,
    requestPriority: draft.requestPriority,
    requestNotes: draft.requestNotes,
    performerId: draft.performerId,
    programId,
    deviceId: null,
    location: null,
    date: now,
    meta: syncableMeta(userId, now),
  });

  return encounterId;
};

export const startOrderedScan = async ({
  encounterCollection,
  examCollection,
  encounterId,
  userId,
  deviceId,
  now,
}: {
  encounterCollection: RxCollection<EpocusEncounter>;
  examCollection: RxCollection<EpocusExam>;
  encounterId: string;
  userId: string;
  deviceId: string | null;
  now: string;
}): Promise<void> => {
  const encounter = await encounterCollection.findOne(encounterId).exec();
  if (encounter == null) {
    throw new Error(`Encounter ${encounterId} not found`);
  }

  const examType = asExamType(encounter.requestedExamType);

  await encounter.incrementalModify((data) => ({
    ...data,
    performerId: userId,
    ...(!data.deviceId && deviceId !== null ? { deviceId } : {}),
    meta: { ...data.meta, updatedAt: now, updatedByUserId: userId },
  }));

  if (examType === null) return;

  const existing = await examCollection
    .find({ selector: { encounterId } })
    .exec();
  if (existing.length > 0) return;

  const normalData = NORMAL_WORKSHEET_BY_TYPE[examType];
  const emptyViews = Object.fromEntries(
    Object.keys(normalData.views).map((view) => [view, false]),
  );

  await examCollection.insert({
    id: nanoid(),
    encounterId,
    examType,
    status: "draft",
    signedAt: null,
    signedByUserId: null,
    nonBillable: false,
    nonBillableReason: null,
    amendments: [],
    worksheetData: JSON.parse(
      JSON.stringify({
        ...normalData,
        indication: usesMultiIndication(examType) ? [] : encounter.indication,
        views: emptyViews,
      }),
    ) as EpocusExam["worksheetData"],
    images: [],
    meta: syncableMeta(userId, now),
  });
};

/**
 * The reason is patched on before the removal, not after: a removed document
 * takes no further patches, so writing it afterwards would silently drop the
 * reason from the tombstone the server revokes the worklist entry from.
 */
export const cancelScanRequest = async ({
  encounterCollection,
  encounterId,
  reason,
  userId,
  now,
}: {
  encounterCollection: RxCollection<EpocusEncounter>;
  encounterId: string;
  reason: string;
  userId: string;
  now: string;
}): Promise<void> => {
  const encounter = await encounterCollection.findOne(encounterId).exec();
  if (encounter == null) {
    throw new Error(`Encounter ${encounterId} not found`);
  }

  const trimmed = reason.trim();
  const notes = [encounter.requestNotes, `Cancelled: ${trimmed}`]
    .filter(Boolean)
    .join("\n");

  const cancelled = await encounter.incrementalModify((data) => ({
    ...data,
    requestNotes: notes,
    meta: { ...data.meta, updatedAt: now, updatedByUserId: userId },
  }));
  await cancelled.remove();
};
